import { create } from "zustand"

import { TRANSFER_FILTERABLE_STATUSES } from "../constants"
import type { TransferSearchParams } from "../schemas"

import { GetTransferListDocument, type GetTransferListQuery, type Transfer } from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

const deduplicateTransfers = (newData: Transfer[], existingData: Transfer[]): Transfer[] => {
  const uniqueIds = new Set(existingData.map((item) => item.transferId))
  return [...existingData, ...newData.filter((item) => !uniqueIds.has(item.transferId))]
}

export const useTransferSearchStore = create(
  createSearchStore<Transfer, TransferSearchParams, GetTransferListQuery>({
    documentNode: GetTransferListDocument,
    deduplicateData: deduplicateTransfers,
    getQueryInput: (filters: TransferSearchParams, offset: number, limit: number) => {
      const {
        field,
        order,
        query,
        types,
        statuses,
        sourceWarehouseAreaIds,
        targetWarehouseAreaIds,
        createdByUserIds,
        createdOnFrom,
        createdOnTo,
      } = filters
      return {
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
        filter: {
          query,
          types: types?.length ? types : undefined,
          statuses: statuses?.length ? statuses : TRANSFER_FILTERABLE_STATUSES,
          sourceWarehouseAreaIds: sourceWarehouseAreaIds?.length ? sourceWarehouseAreaIds : undefined,
          targetWarehouseAreaIds: targetWarehouseAreaIds?.length ? targetWarehouseAreaIds : undefined,
          createdByUserIds: createdByUserIds?.length ? createdByUserIds : undefined,
          createdOnFrom: createdOnFrom ?? undefined,
          createdOnTo: createdOnTo ?? undefined,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.transfer.list.__typename,
      data: data.transfer.list.__typename === "TransferListSuccess" ? (data.transfer.list.items as Transfer[]) : [],
      error: data.transfer.list.__typename === "TransferListFailure" ? data.transfer.list.error : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
